import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { AdminPanelService } from '../../service/admin/admin-panel.service'
import { DdcLoggerService } from '../../service/log/ddc-logger.service'
import { LanguageCode, Languages } from '../../shared/enums/enums'
import { DdcAdminAiConfirmDialogComponent } from './ddc-admin-ai-confirm-dialog.component'

export interface AIAssignCsvToUser {
  csvFile: string
  userId: number
  privacy: string
}

export interface AIDictionaryGenerationRequest {
  userId: number
  wordCount: number
  pairCount: number
  dictionaryType: 'monolingual' | 'bilingual'
  primaryLanguage: string
  secondaryLanguage?: string
  subject: string
  description: string
  privacy: string
  includePos: boolean
  includeArticle: boolean
  includeExample: boolean
  includeDefinition: boolean
  includeCategory: boolean
  includeTags: boolean
}

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
}

@Component({
  selector: 'app-ddc-admin-ai',
  templateUrl: './ddc-admin-ai.component.html',
  styleUrls: ['./ddc-admin-ai.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, AngularMaterialsModules],
})
export class DdcAdminAiComponent implements OnInit, OnDestroy {
  // --- AI-Generated Dictionaries Management ---
  public dictionarySearchTerm: string = ''
  public dictionaries: any[] = []
  public filteredDictionaries: any[] = []
  public isLoadingDics: boolean = false

  /**
   * Load AI-generated dictionaries (stub: replace with real API call)
   */
  loadDictionaries(): void {
    this.isLoadingDics = true
    // TODO: Replace with real API call
    this.adminPanelService.getAIGeneratedDictionaries?.().subscribe({
      next: (dics: any[]) => {
        this.dictionaries = dics || []
        this.filteredDictionaries = this.dictionaries
        this.isLoadingDics = false
      },
      error: err => {
        this.dictionaries = []
        this.filteredDictionaries = []
        this.isLoadingDics = false
      },
    })
  }

  /**
   * Filter dictionaries by search term
   */
  searchDictionaries(): void {
    const term = (this.dictionarySearchTerm || '').toLowerCase()
    this.filteredDictionaries = this.dictionaries.filter(
      d => d.title?.toLowerCase().includes(term) || d.description?.toLowerCase().includes(term),
    )
  }

  /**
   * Delete a dictionary (stub: replace with real API call)
   */
  deleteDictionary(id: any): void {
    const dict = this.dictionaries.find(d => d.id === id)
    const ref = this.dialog.open(DdcAdminAiConfirmDialogComponent, {
      data: { title: dict?.title || 'this dictionary' },
      width: '350px',
      disableClose: true,
    })
    ref.afterClosed().subscribe(result => {
      if (result) {
        this.adminPanelService.deleteAIGeneratedDictionary?.(id).subscribe({
          next: response => {
            // If backend returns status 200 or response.success === true
            if ((response && response.status === 200) || response?.success) {
              this.dictionaries = this.dictionaries.filter(d => d.id !== id)
              this.searchDictionaries()
              this.dialog.open(DdcAdminAiConfirmDialogComponent, {
                data: { title: 'Dictionary deleted successfully!' },
                width: '350px',
                disableClose: false,
              })
            }
          },
          error: () => {
            console.error('Failed to delete dictionary.')
          },
        })
      }
    })
  }

  /**
   * Extend a dictionary (stub: open dialog or emit event)
   */
  extendDictionary(dict: any): void {
    // TODO: Implement extend logic (open dialog, etc.)
    console.error('Extend dictionary: ' + dict.title)
  }
  // Properties for CSV dictionary title and description
  public csvDicTitle: string = ''
  public csvDicDescription: string = ''
  // Properties for assigning CSV dictionary
  assignDicTitle: string = ''
  assignDicDescription: string = ''
  assignDicPrivacy: string = 'public'
  selectedUserId: number = 0

  // Assign loading and feedback states
  isAssigning: boolean = false
  assignError: string = ''
  assignSuccess: string = ''

  // User search and filter
  userSearchTerm: string = ''
  users: User[] = []
  filteredUsers: User[] = []

  filterUsers(searchTerm: string = this.userSearchTerm): void {
    const term = (searchTerm || '').toLowerCase()
    this.filteredUsers = this.users.filter(
      (user: User) =>
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.first_name.toLowerCase().includes(term) ||
        user.last_name.toLowerCase().includes(term),
    )
  }

  onUserSelected(user: User): void {
    this.selectedAssignUser = user.id
    this.userSearchTerm = `${user.username} (${user.email})`
  }
  /**
   * The headers for the CSV table preview. Should be set when CSV is loaded.
   */

  public csvHeaders: string[] = []
  public csvRows: any[] = []
  public csvPage: number = 1
  public csvRowsPerPage: number = 10

  /** Get the rows for the current page */
  public get paginatedCsvRows(): any[] {
    const start = (this.csvPage - 1) * this.csvRowsPerPage
    return this.csvRows.slice(start, start + this.csvRowsPerPage)
  }

  /** Get the total number of pages */
  public get csvTotalPages(): number {
    return Math.ceil(this.csvRows.length / this.csvRowsPerPage) || 1
  }

  /** Go to previous page */
  public prevCsvPage(): void {
    if (this.csvPage > 1) this.csvPage--
  }

  /** Go to next page */
  public nextCsvPage(): void {
    if (this.csvPage < this.csvTotalPages) this.csvPage++
  }

  /** Go to a specific page */
  public goToCsvPage(page: number): void {
    if (page >= 1 && page <= this.csvTotalPages) this.csvPage = page
  }

  /**
   * Call this when a CSV file is loaded as a string. It will parse the CSV and set csvHeaders and csvRows.
   */
  public parseCsv(csvString: string): void {
    // Split into lines
    const lines = csvString.split(/\r?\n/).filter(line => line.trim().length > 0)
    if (lines.length < 2) {
      this.csvHeaders = []
      this.csvRows = []
      this.csvDicTitle = ''
      this.csvDicDescription = ''
      return
    }
    // Parse first line: title and description
    const firstLineCells = lines[0].replace(/^"|"$/g, '').split('";"')
    this.csvDicTitle = firstLineCells[0] || ''
    this.csvDicDescription = firstLineCells[1] || ''

    // Parse headers from second line
    this.csvHeaders = lines[1].replace(/^"|"$/g, '').split('";"')
    // Parse rows from third line onward
    this.csvRows = lines.slice(2).map(line => {
      const cells = line.replace(/^"|"$/g, '').split('";"')
      const rowObj: any = {}
      this.csvHeaders.forEach((header, idx) => {
        rowObj[header] = cells[idx] || ''
      })
      return rowObj
    })
    this.csvPage = 1 // Reset to first page on new load
  }
  // ...existing properties and methods...

  /**
   * Utility: Split a CSV row string by semicolon, handling quotes, for table preview.
   * Accepts either a string (raw CSV line) or an object (parsed row).
   */

  // No longer needed: getCsvRowCells
  // Timer for request duration
  timer = 0
  timerInterval: any = null
  showTimer = false

  // Tab state
  activeTab: 'generate' | 'assign' | 'manage' = 'generate'

  // Watcher for tab change
  setActiveTab(tab: 'generate' | 'assign' | 'manage'): void {
    this.activeTab = tab
    if (tab === 'manage') {
      this.loadDictionaries()
    }
  }

  /**
   * Run the update-ai-generated-index.sh script and reload CSV file list
   * (Requires backend or Electron context; in browser-only Angular, this is not possible.)
   */
  runUpdateIndexScriptAndReload(): void {
    this.adminPanelService.updateAIGeneratedIndex().subscribe({
      next: response => {
        this.loadAvailableCsvFiles()
      },
      error: error => {
        console.error('Error running update index script:', error)
      },
    })
  }

  // CSV upload/assign tab
  selectedAssignUser: number = 0
  availableCsvFiles: string[] = []
  filteredCsvFiles: string[] = []
  selectedCsvFile: string = ''

  filterCsvFiles(searchTerm: string): void {
    const term = (searchTerm || '').toLowerCase()
    this.filteredCsvFiles = this.availableCsvFiles.filter(file => file.toLowerCase().includes(term))
  }

  get timerDisplay(): string {
    const m = Math.floor(this.timer / 60)
    const s = this.timer % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  startTimer(): void {
    this.timer = 0
    this.showTimer = true
    this.timerInterval = setInterval(() => {
      this.timer++
    }, 1000)
  }

  stopTimer(): void {
    this.showTimer = false
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }
  // Loading and error states
  isLoading = false
  error = ''
  success = ''

  // Form data
  formData: AIDictionaryGenerationRequest = {
    userId: 0,
    wordCount: 100,
    pairCount: 200,
    dictionaryType: 'monolingual',
    primaryLanguage: 'ENG',
    secondaryLanguage: '',
    subject: '',
    description: '',
    privacy: 'private',
    includePos: true,
    includeArticle: true,
    includeExample: true,
    includeDefinition: true,
    includeCategory: true,
    includeTags: true,
  }

  // Users list for selection
  showUserDropdown = false

  // Languages
  languages = Languages
  languageKeys = Object.keys(Languages) as (keyof typeof Languages)[]

  // Privacy options
  privacyOptions = [
    { value: 'private', label: 'Private' },
    { value: 'followers', label: 'Followers Only' },
    { value: 'public', label: 'Public' },
  ]

  private subscriptions: Subscription[] = []

  constructor(
    private adminPanelService: AdminPanelService,
    private logger: DdcLoggerService,
    private router: Router,
    private http: HttpClient,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.logger.info('DdcAdminAiComponent: ngOnInit')

    // Check if admin is authenticated
    if (!this.adminPanelService.isAdminAuthenticated()) {
      this.router.navigate(['/admin'])
      return
    }

    this.loadUsers()
    this.loadAvailableCsvFiles()
  }

  /**
   * Load available CSV files from backend via AdminPanelService
   */
  loadAvailableCsvFiles(): void {
    this.adminPanelService.getAvailableCsvFiles().subscribe({
      next: (files: string[]) => {
        this.availableCsvFiles = Array.isArray(files)
          ? files.filter(f => f.endsWith('.csv')).sort((a, b) => a.localeCompare(b))
          : []
        this.filteredCsvFiles = [...this.availableCsvFiles]
      },
      error: err => {
        this.logger.error('Failed to load CSV file list', err)
        this.availableCsvFiles = []
        this.filteredCsvFiles = []
      },
    })
  }

  /**
   * Handle CSV file selection from dropdown
   */
  onCsvFileSelectedFromList(): void {
    // Only load if a real file is selected (not empty string)
    if (!this.selectedCsvFile) {
      this.csvRows = []
      this.csvHeaders = []
      return
    }
    const fileUrl = `/assets/ai-generated/${this.selectedCsvFile}`
    this.http.get(fileUrl, { responseType: 'text' }).subscribe({
      next: text => this.parseCsv(text),
      error: err => {
        this.logger.error('Failed to load selected CSV', err)
        this.csvRows = []
        this.csvHeaders = []
      },
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  /**
   * Load users for selection
   */
  loadUsers(): void {
    this.isLoading = true
    this.error = ''

    const usersSub = this.adminPanelService.getUsersData(1, 100).subscribe({
      next: response => {
        this.isLoading = false
        if (response.success) {
          this.users = response.users
          this.filteredUsers = [...this.users]
        }
      },
      error: err => {
        this.isLoading = false
        this.logger.error('Failed to load users', err)
        this.users = []
        this.filteredUsers = []
      },
    })
    this.subscriptions.push(usersSub)
  }

  /**
   * Select a user from dropdown
   */
  selectUser(user: User): void {
    this.formData.userId = user.id
    this.userSearchTerm = `${user.username} (${user.email})`
    this.showUserDropdown = false
  }

  /**
   * Show user dropdown
   */
  showUsers(): void {
    this.showUserDropdown = true
    this.filterUsers()
  }

  /**
   * Hide user dropdown
   */
  hideUsers(): void {
    setTimeout(() => {
      this.showUserDropdown = false
    }, 200)
  }

  /**
   * Handle dictionary type change
   */
  onDictionaryTypeChange(): void {
    if (this.formData.dictionaryType === 'monolingual') {
      this.formData.secondaryLanguage = ''
    }
  }

  /**
   * Get language name by code
   */
  getLanguageName(code: string): string {
    const language = this.languageKeys.find(key => Languages[key][1] === code)
    return language ? Languages[language][0] : code
  }

  /**
   * Get language code by key
   */
  getLanguageCode(key: keyof typeof Languages): LanguageCode {
    return Languages[key][1]
  }

  /**
   * Validate form
   */
  validateForm(): boolean {
    this.error = ''

    if (!this.formData.userId) {
      this.error = 'Please select a user'
      return false
    }

    if (!this.formData.subject.trim()) {
      this.error = 'Please enter a subject for the dictionary'
      return false
    }

    if (!this.formData.description.trim()) {
      this.error = 'Please enter a description for the dictionary'
      return false
    }

    if (!this.formData.primaryLanguage) {
      this.error = 'Please select a primary language'
      return false
    }

    if (this.formData.dictionaryType === 'bilingual' && !this.formData.secondaryLanguage) {
      this.error = 'Please select a secondary language for bilingual dictionary'
      return false
    }

    if (
      this.formData.dictionaryType === 'bilingual' &&
      this.formData.primaryLanguage === this.formData.secondaryLanguage
    ) {
      this.error = 'Primary and secondary languages must be different'
      return false
    }

    if (this.formData.wordCount < 1) {
      this.error = 'Word count must be at least 1'
      return false
    }

    if (this.formData.dictionaryType === 'bilingual' && this.formData.pairCount < 1) {
      this.error = 'Pair count must be at least 1 for bilingual dictionaries'
      return false
    }

    return true
  }

  /**
   * Submit AI generation request
   */
  submitRequest(): void {
    if (!this.validateForm()) {
      return
    }

    this.isLoading = true
    this.error = ''
    this.success = ''
    this.startTimer()

    const requestSub = this.adminPanelService.generateAIDictionary(this.formData).subscribe({
      next: response => {
        this.isLoading = false
        this.stopTimer()
        if (response.success) {
          this.success = 'AI dictionary generation request submitted successfully!'
          this.resetForm()
        } else {
          this.error = response.message || 'Failed to submit request'
        }
      },
      error: error => {
        this.isLoading = false
        this.stopTimer()
        this.logger.error('Error submitting AI generation request:', error)
        this.error = 'Failed to submit request. Please try again.'
      },
    })

    this.subscriptions.push(requestSub)
  }

  /**
   * Reset form to default values
   */
  resetForm(): void {
    this.formData = {
      userId: 0,
      wordCount: 100,
      pairCount: 200,
      dictionaryType: 'monolingual',
      primaryLanguage: 'ENG',
      secondaryLanguage: '',
      subject: '',
      description: '',
      privacy: 'private',
      includePos: true,
      includeArticle: false,
      includeExample: true,
      includeDefinition: true,
      includeCategory: true,
      includeTags: true,
    }
    this.userSearchTerm = ''
  }

  /**
   * Go back to admin panel
   */
  goBack(): void {
    this.router.navigate(['/admin'])
  }

  /**
   * Assign CSV to selected user (stub)
   */
  assignCsvToUser(): void {
    if (this.isAssigning) return
    this.isAssigning = true
    this.assignError = ''
    this.assignSuccess = ''
    const payload: AIAssignCsvToUser = {
      csvFile: this.selectedCsvFile,
      userId: this.selectedAssignUser,
      privacy: this.assignDicPrivacy,
    }
    this.adminPanelService.assignCsvToUser(payload).subscribe({
      next: response => {
        this.isAssigning = false
        if (response.success) {
          this.assignSuccess = 'Dictionary assigned successfully!'
          this.assignError = ''
        } else {
          this.assignError = response.message || 'Failed to assign dictionary.'
          this.assignSuccess = ''
        }
      },
      error: error => {
        this.isAssigning = false
        this.assignError = 'Error assigning dictionary.'
        this.assignSuccess = ''
      },
    })
  }
}
